// apps/web/src/features/admin/form-config/fields/StageStrip.tsx
import type { FieldContent, HeaderMappingContent, Stage } from '@capture/shared';
import { formatStageLabel, deriveStageRoles } from '@capture/shared';
import { useShallow } from 'zustand/react/shallow';
import { useFormConfigStore } from '../state/store';

const STAGE_ICONS: Record<Stage, string> = {
  'import': '📥',
  'form-recognition': '🔎',
  'batch-fix': '🩹',
  'ocr': '🔤',
  'post-ocr': '🧼',
  'key-entry': '✏️',
  'verification': '✅',
  'qa': '🔍',
  'supervisor': '👁',
  'pre-export': '📦',
  'export': '📤',
};

const EMPTY_STAGES: Stage[] = [];

interface StageStripProps {
  stageMap: FieldContent['stageMap'];
  /**
   * R-194 — the field's identity + modern OCR signals + the JT's header
   * mapping let the strip render the lifecycle-ROLE (outline) indicator in
   * addition to the behavior (fill) indicator. Optional so the many
   * existing call sites (and read-only contexts) that only care about
   * behavior keep compiling — without them the strip degrades to the
   * pre-R-194 behavior-only rendering.
   *
   * OCR participation is the MODERN signal (R-123, 2026-05-23): a field is
   * OCR-populated when `ocrExtracted` is true or it carries `ocrAliases`.
   * The legacy `ocrFieldMapping` column is no longer populated for
   * OCR-Profiles-configured fields and is NOT consulted here.
   */
  internalName?: string;
  ocrExtracted?: boolean;
  ocrAliases?: string[];
  headerMapping?: HeaderMappingContent | null;
}

/**
 * R-194 — dual indicator. Each dot encodes two facts:
 *   - fill (`--filled`)  = an operator BEHAVIOR is configured at this
 *     stage (`stageMap[stage].length > 0`).
 *   - outline (`--role`) = the field has a lifecycle ROLE at this stage
 *     (OCR populates it / import stamps it) even when no behavior is
 *     configured. Derived by `deriveStageRoles` — only `ocr` and
 *     `import` ever carry a role distinct from their behavior.
 *
 * Render precedence per dot: filled → `--filled`; else role → `--role`
 * (outlined); else unlit. Tooltip:
 *   - behavior  → "Key Entry — active"
 *   - role-only → "OCR — populates this field (no behaviors configured)"
 *   - neither   → "OCR — inactive"
 */
export function StageStrip({
  stageMap,
  internalName,
  ocrExtracted,
  ocrAliases,
  headerMapping,
}: StageStripProps) {
  const pipelineStages = useFormConfigStore(useShallow((s) => s.content?.pipeline.stages ?? EMPTY_STAGES));
  const roles = deriveStageRoles({ internalName, ocrExtracted, ocrAliases }, headerMapping);

  return (
    <span className="stage-strip" aria-label="Stage participation">
      {pipelineStages.map((s) => {
        const filled = (stageMap[s] ?? []).length > 0;
        const role = !filled && roles.has(s as 'ocr' | 'import');
        const label = formatStageLabel(s);
        // R-170 / R-194 — tooltip reads as a sentence and now
        // distinguishes a configured behavior (active) from a pure
        // lifecycle role (populates this field) from a dead dot
        // (inactive). Operators can hover any dot for a legend.
        const tooltip = filled
          ? `${label} — active`
          : role
            ? `${label} — populates this field (no behaviors configured)`
            : `${label} — inactive`;
        const cls = [
          'stage-strip__icon',
          filled ? 'stage-strip__icon--active stage-strip__icon--filled' : '',
          role ? 'stage-strip__icon--role' : '',
        ]
          .filter(Boolean)
          .join(' ');
        return (
          <span
            key={s}
            className={cls}
            title={tooltip}
            aria-label={tooltip}
          >
            {filled || role ? STAGE_ICONS[s] : '·'}
          </span>
        );
      })}
    </span>
  );
}
